import React, { useState } from 'react';
import './BanProposalModal.css';

/**
 * BanProposalModal Component (Boycott Feature)
 *
 * Modal for users to boycott an event - reporting concerns to moderators
 * who may review and share with attendees to keep communities safe.
 */
const BanProposalModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  alreadyProposed = false,
  eventTitle = '',
}) => {
  const [reason, setReason] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  const banReasons = [
    { value: '', label: 'Select a reason...' },
    { value: 'safety_concerns', label: 'Safety concerns at the event' },
    { value: 'fraudulent', label: 'Fraudulent or scam event' },
    { value: 'inappropriate_content', label: 'Inappropriate content/activities' },
    { value: 'repeated_issues', label: 'Repeated issues across multiple editions' },
    { value: 'organizer_misconduct', label: 'Organizer misconduct' },
    { value: 'discrimination', label: 'Discrimination or exclusion' },
    { value: 'other', label: 'Other (please explain)' },
  ];

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedReason) {
      alert('Please select a reason for your boycott report');
      return;
    }
    if (reason.trim().length < 150) {
      alert('Please provide more details (at least 150 characters) to help us review your report');
      return;
    }
    onSubmit(selectedReason, reason.trim());
  };

  const handleClose = () => {
    setReason('');
    setSelectedReason('');
    onClose();
  };

  return (
    <div className="ban-modal-overlay" onClick={handleClose}>
      <div className="ban-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="ban-modal-close" onClick={handleClose}>
          <i className="bi bi-x-lg"></i>
        </button>

        <div className="ban-modal-header">
          <i className="bi bi-slash-circle"></i>
          <h3>I Boycott This Event</h3>
        </div>

        {alreadyProposed ? (
          <div className="ban-already-proposed">
            <i className="bi bi-check-circle"></i>
            <p>You have already submitted a boycott report for this event.</p>
            <p className="ban-note">Our team will review your report.</p>
          </div>
        ) : (
          <>
            <p className="ban-modal-subtitle">
              Reporting: <strong>{eventTitle}</strong>
            </p>
            <p className="ban-modal-description">
              To keep our communities safe and informed. The information will be reviewed
              by the moderators and a warning might potentially be shared with the attendees.
            </p>
            <p className="ban-modal-instruction">
              Please include as much information and public references as possible for us
              to consider this reliable.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="ban-form-group">
                <label>Primary concern</label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="ban-select"
                  required
                >
                  {banReasons.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ban-form-group">
                <label>Detailed information</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Provide specific details, dates, incidents, public references, or any evidence available. The more information you include, the better we can review your report..."
                  rows={6}
                  maxLength={2000}
                  required
                />
                <div className="ban-char-info">
                  <span className={reason.length < 150 ? 'ban-char-warning' : 'ban-char-count'}>
                    {reason.length}/2000 {reason.length < 150 && `(min. 150 characters)`}
                  </span>
                </div>
              </div>

              <div className="ban-modal-actions">
                <button
                  type="button"
                  className="ban-btn-cancel"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ban-btn-submit"
                  disabled={isSubmitting || !selectedReason || reason.trim().length < 150}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-tiny"></span> Submitting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send"></i> Submit Report
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default BanProposalModal;
